import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { AbstractToggleFormComponent, EditablePanelComponent, NoResultComponent } from '@spike/scaffolds';
import { FormFactoryService } from '../../../shared/services/form-factory.service';
import { FormArray, ReactiveFormsModule } from '@angular/forms';
import { AsyncApiService } from '@spike/core';
import { WorkflowApiService } from '../../../workflow/services/workflow-api.service';
import { EquipmentRequestsTableComponent } from "../../components/equipment-requests-table/equipment-requests-table";
import { CreateEquipmentRequestSidePanelComponent } from "../create-equipment-request-side-panel/create-equipment-request-side-panel";
import { PositionMaterialsFormComponent } from "../../forms/position-materials-form/position-materials-form";
import { PositionToolsFormComponent } from "../../forms/position-tools-form/position-tools-form";
import { PositionOperationsFormComponent } from "../../forms/position-operations-form/position-operations-form";
import { ConfirmationDialogService } from '../../../shared/services/confirmation-dialog.service';
import { WorkPhase } from 'src/app/features/workflow/models/workphase.model';
import { WorkflowState } from 'src/app/features/workflow/models/workflow-status.enum';
import { EquipmentRequest } from '../../models/equipment-request.model';
import { SortingModel } from 'src/app/features/shared/models/sorting.model';
import { EntityApiEnum } from 'src/app/config/entity-api.enum';
import { Operation } from '../../models/operation.model';
import { Material } from '../../models/material.model';
import { Tool } from '../../models/tool.model';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'spike-working-phase-toggle-panel',
  providers:[],
  imports: [EquipmentRequestsTableComponent, CreateEquipmentRequestSidePanelComponent, PositionMaterialsFormComponent, PositionToolsFormComponent, PositionOperationsFormComponent, EditablePanelComponent, ReactiveFormsModule, NoResultComponent],
  templateUrl: './working-phase-toggle-panel.html'
})
export class WorkingPhaseTogglePanel extends AbstractToggleFormComponent<WorkPhase> {
  // Inject
  private readonly workflowApi = inject(WorkflowApiService);
  private readonly asyncApi = inject(AsyncApiService);
  private readonly formFactory = inject(FormFactoryService);
  private readonly confirmationDialogService = inject(ConfirmationDialogService);

  // Input
  public readonly data = input.required<WorkPhase>();
  public readonly workflowState= input.required<WorkflowState>();

  // Output
  public readonly onSave = output<WorkPhase|undefined>();

  // State
  public operationsReady = signal<boolean>(false);
  public toolsReady = signal<boolean>(false);
  public materialsReady = signal<boolean>(false);
  public isRequestDialogVisible = signal<boolean>(false);
  public isPrintDialogVisible = signal<boolean>(false);
  public selectedRequest = signal<EquipmentRequest|undefined>(undefined);
  public page= signal<number>(0);
  public readonly sorting = signal<SortingModel>({});
  
  //forms
  public readonly form = this.formFactory.createWorkphaseForm();

  public readonly workingPhase= computed(()=>{
    const workflowState =this.workflowState();
    return workflowState.workingPhase!;
  })

  override readonly disabledFields= computed(()=>{
    const data = this.data();
    const workflowState = this.workflowState();
    if(!data || !workflowState) return [];
    return workflowState?.list?.find(x=>x.id == data?.stateId)?.disabledFields??[];
  })

  public readonly isReadOnly= computed(()=>{
    const data = this.data();
    const workflowState = this.workflowState();
    if(!data || !workflowState) return false;
    return !!workflowState?.list?.find(x=>x.id == data?.stateId)?.readonly;
  })

  public readonly canRequest = computed(()=>{
    const data = this.data();
    const workingPhase = this.workingPhase(); 
    return workingPhase == 3 && data?.stateId! < 4;
  })

  public readonly canEditRequest = computed(()=>{
    const data = this.data();
    const workingPhase = this.workingPhase(); 
    const selectedRequest = this.selectedRequest();
    return workingPhase == 3 && data?.stateId! < 4 && (!selectedRequest?.stateId || selectedRequest?.stateId == 1);
  })

 public readonly showConsuntive = computed(()=>{
    const data = this.data();
    const workingPhase = this.workingPhase(); 
    return workingPhase == 3 && data?.stateId! >= 5;
  })

  changeGrandTotal() {
    const control = this.form.get('totalCalculated');
    const toolCost = this.form.get('toolCost')?.value ?? 0;
    const materialCost = this.form.get('equipmentCost')?.value ?? 0;  
    const productionCost = this.form.get('productionCost')?.value ?? 0;  
    const externalCost = this.form.get('externalCost')?.value ?? 0;  
    const totalAmount = toolCost + materialCost + externalCost + productionCost;
    if (control && control.value !== totalAmount) {
      control.patchValue(parseFloat(totalAmount.toFixed(2)), {emitEvent: false, onlySelf: true });
    }
  }

  changeTotal(controlName: string, amount: number){
    const control = this.form.get(controlName);
    if (control && control.value !== amount) {
      control.patchValue(parseFloat(amount.toFixed(2)), {emitEvent: false,onlySelf: true});
      this.changeGrandTotal();
    }
  }

  async save() {
    this.form.markAllAsTouched();
    if (this.form.valid) {
      const generalValues = this.form.getRawValue();
      const toSaveData: any = {
        ...generalValues,
      };

      await this.asyncApi.save<WorkPhase>(EntityApiEnum.WORKPHASE, toSaveData, this.data()!.phaseId);
      this.onUpdate();
    }
  }

  public onUpdate(){
    this.isRequestDialogVisible.set(false)
    this.onSave.emit(this.data())
  }

  get operationsArray(): FormArray { return this.form.get('operations') as FormArray;  }
  get toolsArray(): FormArray { return this.form.get('tools') as FormArray;  }
  get materialsArray(): FormArray { return this.form.get('materials') as FormArray;  }

  protected override buildFormArrays(data: WorkPhase): void {
    
    this.buildOperationsArray(data);
    this.buildToolsArray(data);
    this.buildMaterialsArray(data);
  }

  protected buildOperationsArray(data: WorkPhase): void {
    this.fillFormArray(this.operationsArray, data.operations,  (op) => this.formFactory.createPositionOperationForm(op));
    this.operationsReady.set(true);
  }
  protected buildMaterialsArray(data: WorkPhase): void {
    this.fillFormArray(this.materialsArray, data.materials, (mat) => this.formFactory.createMaterialForm(mat));
    this.materialsReady.set(true);
  }

  protected buildToolsArray(data: WorkPhase): void {
    this.fillFormArray(this.toolsArray, data.tools, (tool) => this.formFactory.createToolForm(tool));
    this.toolsReady.set(true);
  }



  addOperationExternalRow() {
    const external= this.formFactory.createPositionOperationForm({workType:2, sequenzaId: this.operationsArray.length+1}  as Operation);
    this.operationsArray.push(external);
  }

  addOperationInternalRow() {
    const internal= this.formFactory.createPositionOperationForm({workType:1, sequenzaId: this.operationsArray.length+1} as Operation);
    this.operationsArray.push(internal);
  }

  deleteOperationRow(index: number) {
    this.operationsArray.removeAt(index);
  }

  deleteToolRow(index: number) {
    this.toolsArray.removeAt(index);
  }

  deleteMaterialRow(index: number) {
    this.materialsArray.removeAt(index);
  }

   addMaterialRow() {
    this.materialsArray.push(this.formFactory.createMaterialForm());
  }

   addToolRow() {
    this.toolsArray.push(this.formFactory.createToolForm());
  }

  createRequest(equip: Material|Tool){
    const data = this.data();
    let newEquipReq : EquipmentRequest = {};
    newEquipReq.positionId = data.positionId;
    newEquipReq.orderId = data.orderId;
    newEquipReq.equipmentId = equip.equipmentId;
    newEquipReq.operations = structuredClone(data.operations); 
    this.selectedRequest.set(newEquipReq);
    this.isRequestDialogVisible.set(true);
  }

  onRequestSelected(eqipreq: EquipmentRequest){
    const data = this.data();
    const dataops = structuredClone(data.operations??[]); 
    let operations = eqipreq.operations;
    dataops.forEach(dataop => {
      dataop.selected= !!operations?.find(x=>x.operationId == dataop.operationId);
    });
    eqipreq.operations= dataops;
    this.selectedRequest.set(eqipreq);
    this.isRequestDialogVisible.set(true);
  }


  async onRequestDeleted(data: EquipmentRequest){
    this.confirmationDialogService.show('Sei sicuro di voler eliminare questa richiesta materiale?', async ()=>{
      await this.workflowApi.deleteEquipmentRequest(data);
      this.onSave.emit(this.data())
    });
  }
  
   
}